<script setup>
  import { computed, ref, watch } from "vue";

  import api, { getUserInfo, setUserInfo } from "@/lib/api";
  import { TEXT } from "@/utils/constant";
  import UiModal from "@/components/UiModal.vue";

  const props = defineProps({
    show: {
      type: Boolean,
      required: true,
    },
  });

  const emits = defineEmits(["update:show", "cancel"]);

  const localShow = computed({
    get: () => props.show,
    set: (val) => emits("update:show", val),
  });

  const closeModal = () => {
    emits("update:show", false);
  };

  const handleCancel = () => {
    emits("cancel");
  };

  const privacyContent = ref("");
  const policyId = ref(null);
  const checkedAgree = ref(false);
  const isLoading = ref(false);
  const isSubmitting = ref(false);

  const fetchPrivacyContent = async () => {
    if (isLoading.value) return;

    isLoading.value = true;
    try {
      const response = await api.post(
        `${process.env.VUE_APP_API_BASE_URL}/account/getAgreePolicyContent`,
      );

      privacyContent.value = response.data?.result?.content || "";
      policyId.value = response.data?.result?.policyId || null;
    } catch (error) {
      console.error("Failed to fetch privacy content:", error);
      // Fallback to default content if API fails
      privacyContent.value = `
  <strong>서울 학생 미래역량 진단 시험 개인정보 수집·이용 동의서(교원용)</strong>
  <p>
    서울특별시교육청은「개인정보보호법」제15조(개인정보의 수집·이용),
    제17조(개인정보의 제공), 제18조(개인정보의 이용·제공 제한),
    제22조(동의를 받는 방법)에 의거 서울 학생 미래역량 진단 시험 운영 및
    검사 서비스 관리를 위해 아래와 같이 교원의 개인정보를 수집·이용하고자
    합니다. 다음 사항을 충분히 읽어보신 후 동의 여부에 체크하여 주시기
    바랍니다.
  </p>
  <strong>□ 개인정보 수집·이용에 관한 사항(필수)</strong>
  <table>
    <colgroup>
      <col style="width: 100px" />
      <col style="width: auto" />
      <col style="width: auto" />
      <col style="width: auto" />
    </colgroup>
    <thead>
      <tr>
        <th style="text-align: center">구분</th>
        <th style="text-align: center">수집·이용 목적</th>
        <th style="text-align: center">수집 항목</th>
        <th style="text-align: center">보유 및<br />이용 기간</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="text-align: center; white-space: normal">
          기본<br />정보
        </td>
        <td style="text-align: center; white-space: normal">
          서울 학생 미래역량 진단 서비스 관리, 사용자 인증 등 계정관리
        </td>
        <td style="text-align: center; white-space: normal">
          아이디, 이름, 소속코드, 소속명, 담임정보(학년, 반), 이메일,
          전화번호(인공지능 맞춤형 교수학습 플랫폼에서 제3자 제공으로 받은 정보)
        </td>
        <td style="text-align: center; white-space: normal">
          <strong> 회원탈퇴 또는 퇴직시 까지 </strong>
        </td>
      </tr>
      <tr>
        <td style="text-align: center">추가<br />항목</td>
        <td style="text-align: center; white-space: normal">
          서울 학생 미래역량 진단 검사시스템 운영 및 기술지원
        </td>
        <td style="text-align: center; white-space: normal">
          직무, 보직, 접속정보(접속IP, 접속일시, 시험감독 로그)
        </td>
        <td style="text-align: center; white-space: normal">
          <strong> 회원탈퇴 또는 퇴직시 까지 </strong>
        </td>
      </tr>
    </tbody>
  </table>
  <p>
    ※ 정보주체는 개인정보 수집 및 이용에 대한 동의를 거부할 권리가 있습니다.
    단, 동의를 거부할 경우 서울 학생 미래역량 진단 검사시스템 운영에 제한을
    받을 수 있습니다.
  </p>
`;
    } finally {
      isLoading.value = false;
    }
  };

  const submitModal = async () => {
    if (isSubmitting.value) return;

    isSubmitting.value = true;
    try {
      await api.post(
        `${process.env.VUE_APP_API_BASE_URL}/account/agreePolicy`,
        {
          policyId: policyId.value,
        },
      );

      // 성공 후 사용자 정보 업데이트
      const currentUser = getUserInfo();
      setUserInfo({ ...currentUser, isAgreedValid: "Y" });

      emits("update:show", false);
    } catch (error) {
      console.error("Failed to submit privacy agreement:", error);
      alert("개인정보 동의 처리 중 오류가 발생했습니다. 다시 시도해 주세요.");
    } finally {
      isSubmitting.value = false;
    }
  };

  // Watch for modal show changes to fetch content
  watch(
    () => props.show,
    (newShow) => {
      if (newShow && !privacyContent.value) {
        fetchPrivacyContent();
      }
    },
    { immediate: true },
  );
</script>

<template>
  <ui-modal
    v-model:show="localShow"
    title="개인정보 수집·이용 동의"
    :close-on-esc="false"
    :mask-closable="false"
    :closable="false"
  >
    <template #body>
      <div v-if="isLoading" class="p-4 text-center">로딩 중...</div>
      <div
        v-else
        class="board-detail-content whitespace-pre-line"
        v-html="privacyContent"
      ></div>
    </template>
    <template #footer>
      <n-checkbox
        size="large"
        v-model:checked="checkedAgree"
        style="--n-label-font-weight: 600"
        label="위 내용을 충분히 읽고 이해하였으며, 개인정보 수집·이용에 동의합니다."
      >
      </n-checkbox>
      <div class="btn-area mt-4">
        <ui-button
          variant="outlined"
          color="gray"
          class="basis-[160px]"
          @click="handleCancel"
          :disabled="isSubmitting"
          >취소
        </ui-button>
        <ui-button
          class="flex-1"
          :disabled="!checkedAgree || isSubmitting"
          :loading="isSubmitting"
          @click="submitModal"
          >동의합니다.</ui-button
        >
      </div>
    </template>
  </ui-modal>
</template>
